import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import classNames from "classnames";
import produce from "immer";

import ProductImage from "../../ProductImage";
import shallow from "zustand/shallow";
import PDPConfiguratorLoader from "../PDPConfiguratorLoader"
import Collapsible from "../../Collapsible";
import Radio from "../../forms/Radio";

import { log } from "@assets/js/utils";
import getUniqueId from "@assets/js/get-unique-id";
import { CONFIGURATOR_MODE, IMAGE, STATUS, CONFIGURATOR_SELECTOR, CONFIGURATOR_TYPE } from "@constants";
import { ArrowSmallSvg } from "@assets/js/icons";

import usePDPStore, {
  useConfiguringPendingProductData,
  usePendingProductData,
  useConfiguratorMode,
  useConfigurationType,
  useActiveModelData,
  useMaterialSets,
  useModelTypes,
  useActiveSelector,
  useActiveMaterialIndex
} from "@store/pdp";

import useGlobalStore, { useCommerceMarket } from "@store/global";

import "./pdp-configurator-options.scss";

const PDPConfiguratorOptionsWrapper = () => {
  const [configuringPendingProductData] = useConfiguringPendingProductData();
  const [commerceMarket] = useCommerceMarket();
  const [configuratorMode] = useConfiguratorMode();
  const [configurationType] = useConfigurationType();
  const [, , fetchMaterialSets] = useMaterialSets();
  const fetchLoadingStatus = usePDPStore(
    (state) => state.loadStatus
  );

  const [, , fetchModelTypes] = useModelTypes();
  const [, setActiveSelector] = useActiveSelector();
  const [, setActiveMaterialIndex] = useActiveMaterialIndex();
  const [, sc_lang] = useGlobalStore(
    (state) => [state.currentSiteMode, state.sc_lang],
    shallow
  );

  const showConfiguratorLoader = () => {
    var isSkuProduct = configuratorMode === CONFIGURATOR_MODE.NONE;
    return fetchLoadingStatus === STATUS.LOADING && !isSkuProduct;
  };
  const activateModelOptionHandler = (model) => {
    fetchModelTypes(model.groupId, configuringPendingProductData.model.configurationId);

    setActiveSelector(CONFIGURATOR_SELECTOR.MODEL_TYPES);
  };

  const activateMaterialOptionHandler = (materialIndex) => {
    setActiveMaterialIndex(materialIndex);
    
    let variationGroupId
    if(configurationType?.type?.value === CONFIGURATOR_TYPE.VARIATION_GROUP){
      variationGroupId = configurationType.id.value;
    }
    
    fetchMaterialSets(
      materialIndex,
      configuringPendingProductData.model.typeId,
      configuringPendingProductData.info.materialSetGroupingEnabled,
      configuringPendingProductData.materials,
      sc_lang,
      commerceMarket,
      variationGroupId
    );

    setActiveSelector(CONFIGURATOR_SELECTOR.MATERIAL_SETS);
  };
  return showConfiguratorLoader() ?
    (<PDPConfiguratorLoader />) :
    (<PDPConfiguratorOptions
      activateModelHandler={activateModelOptionHandler}
      activateMaterialHandler={activateMaterialOptionHandler}
    />)

}

const PDPConfiguratorOptions = ({
  activateModelHandler,
  activateMaterialHandler,
}) => {
  const { t } = useTranslation();
  const texts = {
    editModel: t("FritzHansen_Project_ConsumersAndProfessionals_PDP_EditModel"),
    editMaterial: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_EditMaterial"
    ),
    edit: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_Edit"
    ),
    selectConfiguration: t(
      "FritzHansen_Project_ConsumersAndProfessionals_PDP_SelectConfiguration"
    )
  };
  const [configuratorMode] = useConfiguratorMode();
  const [activeModelData] = useActiveModelData();
  const [configuringPendingProductData] = useConfiguringPendingProductData();
  const [pendingProductData, setPendingProductData] = usePendingProductData();

  const getButtonLabel = (index) => {
    if (configuringPendingProductData) {
      if (configuringPendingProductData?.configurationTypes && configuringPendingProductData.configurationTypes[index]) {
        return `${texts.edit} ${(configuringPendingProductData.configurationTypes[index])}`;
      }
    }
    return `${texts.editMaterial} ${configuringPendingProductData.materials.length > 1 ? index + 1 : ""}`;
  }

  const getOptionsLabel = (index, includeEditText = true) => {
    if (configuringPendingProductData) {
      if (configuringPendingProductData.optionTypes && configuringPendingProductData.optionTypes[index]) {
        if(includeEditText){
          return `${texts.edit} ${(configuringPendingProductData.optionTypes[index]?.split('|')[0])}`;
        }
        return configuringPendingProductData.optionTypes[index]?.split('|')[0];
      }
    }
    return "";
  }

  const selectOptionHandler = (item, selectedOption, index) => {
    var newPendingProductData = produce(pendingProductData, (tmpModel) => {
      tmpModel.options[index] = {
        optionGroupId: item.optionGroupId,
        optionGroupLabel: item.optionGroupLabel,
        isSelectable: item.isSelectable,
        isCheckbox: item.isCheckbox,
        activeOptionId: selectedOption.optionId,
        activeOptionLabel: selectedOption.optionLabel,
        availableOptions: item.availableOptions
      }
      tmpModel.selectedOptions[index] = `${selectedOption.optionLabel}|${selectedOption.optionId}`;
    });

    let newConfigurationId = newPendingProductData.model.configurationId;
    let existingConfigurationIdParts = newPendingProductData.model.configurationId.split('_');
    newConfigurationId = existingConfigurationIdParts[0] + "_";
    newPendingProductData.selectedOptions.forEach((item) => {
      newConfigurationId += `-${item.split('|')[1]}`
    });

    newPendingProductData = produce(newPendingProductData, (tmpModel) => {
      tmpModel.model.configurationId = newConfigurationId
    });

    setPendingProductData(newPendingProductData);
  }

  return (
    <div className="pdp-configurator-options">
      <h3>{texts.selectConfiguration}</h3>
      {configuratorMode !== CONFIGURATOR_MODE.NONE && (
        <div className="pdp-configurator-options__inner">
          {configuratorMode === CONFIGURATOR_MODE.ADVANCED &&
            configuringPendingProductData &&
            configuringPendingProductData?.model && (
              <button
                className="pdp-configurator-options__item"
                onClick={() => activateModelHandler(configuringPendingProductData.model)}
              >
                <div>
                  <h5>{texts.editModel}</h5>
                  <p>
                    {configuringPendingProductData.model.label ||
                      `${configuringPendingProductData.model.groupId} model ${configuringPendingProductData.model.typeId}`}
                  </p>
                </div>
                {configuringPendingProductData?.model?.configurationId && (
                  <ProductImage
                    imageId={configuringPendingProductData.model.configurationId}
                    alt={configuringPendingProductData.model.typeId}
                    sw="35"
                    backgroundColor="ffffff"
                    useCDNUrl={configuringPendingProductData.UseImageCDNUrl}
                  />
                )}
              </button>
            )}

          {configuringPendingProductData?.materials &&
            configuringPendingProductData.materials.map((item, index) => {
              let displayImage;
              if (!item.activeMaterial.uniqueMaterial) {
                displayImage = <ProductImage
                  type={IMAGE.SWATCH}
                  imageId={item.activeMaterial.materialId}
                  alt={item.activeMaterial.materialLabel}
                  sw={"35"} cx="350" cy="350" ch="150" cw="150"
                />
              }
              else {
                displayImage = <ProductImage
                  type={IMAGE.SWATCH}
                  imageUrl={item.activeMaterial.uniqueMaterial.imageUrl}
                  alt={item.activeMaterial.materialLabel}
                  sw={"35"}
                />
              }
              if (!item.activeMaterial.shouldBeHidden) {
                return (
                  <button
                    key={getUniqueId()}
                    className="pdp-configurator-options__item"
                    onClick={() => activateMaterialHandler(index, item)}
                  >
                    <div>
                      <h5>
                        {getButtonLabel(index)}
                      </h5>
                      {item?.activeMaterial?.primaryMaterialSetLabel &&
                        item?.activeMaterial?.primaryMaterialSetLabel?.length > 0 ? (
                        <p>
                          {item.activeMaterial.primaryMaterialSetLabel +
                            " / " +
                            item.activeMaterial.materialLabel}
                        </p>
                      ) : item?.activeMaterial?.materialSetLabel &&
                        item?.activeMaterial?.materialSetLabel?.length > 0 ? (
                        <p>
                          {item.activeMaterial.materialSetLabel +
                            " / " +
                            item.activeMaterial.materialLabel}
                        </p>
                      ) : <p>{item.activeMaterial.materialLabel}</p>}
                    </div>
                    {displayImage}
                  </button>
                );
              } return <></>;
            })}
          
          {pendingProductData?.options &&
            pendingProductData.options.map((item, index) => {
              if (item.isSelectable && item.isCheckbox) {
                return <div key={`configurator-options-${index}`} className="pdp-configurator-options__item">
                  <div className="pdp-configurator-options__radio-list">
                    <h5>
                      {getOptionsLabel(index, false)}
                    </h5>
                    {item.availableOptions.map((a, index2) => {
                      let classList = classNames({
                        "is-active": a.optionId == item.activeOptionId
                      });
                      return <Radio
                        key={`option-radio-${item.optionGroupId}-${index2}`}
                        name={`option-radio-${item.optionGroupId}`}
                        id={`methodRadio${item.optionGroupId}${index2}`}
                        value={a.optionId}
                        label={a.optionLabel}
                        checked={a.optionId == item.activeOptionId}
                        changeHandler={() => selectOptionHandler(item, a, index)}
                        className={classList}
                    />
                    })}
                  </div>
                </div>
              }
              else if (item.isSelectable) {
                return <Collapsible
                  key={`configurator-options-${index}`}
                  preExpanded={false}
                  triggerComponent={
                    <div className="pdp-configurator-options__collapsible-item">
                      <div>
                        <h5>
                          {getOptionsLabel(index)}
                        </h5>
                        <p>{item.activeOptionLabel}</p>
                      </div>
                      <ArrowSmallSvg />
                    </div>
                  }
                  contentComponent={
                    item.availableOptions.map((a) => {
                      let classList = classNames({
                        "is-active": a.optionId == item.activeOptionId
                      });
                      return <button key={`configurator-options-${a.optionId}`} onClick={() => selectOptionHandler(item, a, index)} className={classList}>{a.optionLabel}</button>
                    })
                  }
                  disableTransition={true}
                />
              } else {
                return <Fragment key={`configurator-options-${index}`} />;
              }
            })}
        </div>
      )}
    </div>
  );
};

PDPConfiguratorOptions.propTypes = {
  activateModelHandler: PropTypes.func.isRequired,
  activateMaterialHandler: PropTypes.func.isRequired,
};

export default PDPConfiguratorOptionsWrapper;
